import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Crown, Zap, Armchair, ArrowRightLeft, Radio } from "lucide-react";
import { api } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import FDRPill from "@/components/app/FDRPill";
import { T } from "@/lib/testIds";
import { cn } from "@/lib/utils";

const positionRow = (element_type) => ({ 1: 0, 2: 1, 3: 2, 4: 3 }[element_type] ?? 2);

// Same chip convention as the Standings tap modal (TeamDetailModal).
const CHIP_TONE = {
  "3xc": { icon: Crown, tone: "gold", label: "Triple Captain played" },
  bboost: { icon: Armchair, tone: "green", label: "Bench Boost played" },
  freehit: { icon: Zap, tone: "amber", label: "Free Hit played" },
  wildcard: { icon: ArrowRightLeft, tone: "red", label: "Wildcard played" },
};

function ChipBadge({ chip }) {
  const meta = CHIP_TONE[chip];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div
      data-testid="myteam-chip-badge"
      className={cn(
        "inline-flex items-center gap-2 rounded-full px-3 py-1.5 border mono uppercase text-[10px] tracking-widest",
        meta.tone === "gold" && "border-totem-gold/50 text-totem-gold bg-totem-gold/10",
        meta.tone === "green" && "border-totem-green/50 text-totem-green bg-totem-green/10",
        meta.tone === "amber" && "border-totem-amber/50 text-totem-amber bg-totem-amber/10",
        meta.tone === "red" && "border-totem-red/50 text-totem-red bg-totem-red/10",
      )}
    >
      <Icon size={12} />
      {meta.label}
    </div>
  );
}

function CaptainPoints({ pick }) {
  return (
    <span
      data-testid={`myteam-points-${pick.id}`}
      className="mono text-[9px] font-bold whitespace-nowrap"
    >
      <span className="text-totem-textDim">{pick.raw_points}</span>
      <span className="text-totem-textFaint mx-0.5">→</span>
      <span className="text-totem-gold">{pick.multiplied_points}</span>
      <span className="text-totem-gold text-[8px]"> (C)</span>
    </span>
  );
}

function StandardPoints({ pick }) {
  const benched = pick.multiplier === 0;
  const value = benched ? pick.raw_points : pick.multiplied_points;
  return (
    <span
      data-testid={`myteam-points-${pick.id}`}
      className={cn(
        "mono text-[10px] font-bold",
        benched ? "text-totem-textFaint" : "text-totem-green"
      )}
    >
      {value}
    </span>
  );
}

function FixtureChip({ pick }) {
  const fx = pick.next_fixture;
  let venue = "";
  if (fx) venue = fx.is_home ? " (H)" : " (A)";
  return (
    <>
      <span className="mono text-[9px] text-totem-textDim uppercase">
        {fx?.opponent_short || pick.team_short}{venue}
      </span>
      {fx?.difficulty ? (
        <FDRPill difficulty={fx.difficulty} label={String(fx.difficulty)} />
      ) : (
        <span className="mono text-[9px] text-totem-green">{pick.form}</span>
      )}
    </>
  );
}

function PlayerBadge({ pick }) {
  const showLivePoints = pick.is_live || pick.is_finalized;
  if (!showLivePoints) return <FixtureChip pick={pick} />;
  if (pick.is_captain && pick.multiplier > 1) return <CaptainPoints pick={pick} />;
  return <StandardPoints pick={pick} />;
}

const PlayerCard = ({ pick }) => (
  <Link
    to={`/players/${pick.id}`}
    className={cn(
      "flex flex-col items-center flex-1 min-w-0 max-w-[86px]",
      pick.multiplier === 0 && "opacity-50"
    )}
    data-testid={`myteam-player-${pick.id}`}
  >
    <div className="relative">
      <PlayerAvatar player={pick} size={54} showRing={pick.is_captain} />
      {pick.is_captain && (
        <span className="absolute -bottom-1 -right-1 grid place-items-center w-5 h-5 rounded-full bg-totem-gold text-totem-bg mono text-[10px] font-bold border border-totem-bg">
          C
        </span>
      )}
      {pick.is_vice_captain && !pick.is_captain && (
        <span className="absolute -bottom-1 -right-1 grid place-items-center w-5 h-5 rounded-full bg-totem-surfaceHigh text-totem-textDim mono text-[10px] font-bold border border-totem-bg">
          V
        </span>
      )}
      {pick.is_live && (
        <span
          data-testid={`myteam-live-dot-${pick.id}`}
          className="absolute -top-0.5 -left-0.5 w-2 h-2 rounded-full bg-totem-red animate-pulse ring-2 ring-totem-bg"
          title="Live"
        />
      )}
    </div>
    <div className="mt-1 bg-totem-surfaceElev/95 border border-totem-border rounded-md px-1.5 py-1 w-full text-center">
      <div className="mono text-[10px] font-semibold truncate">{pick.web_name}</div>
      <div className="flex items-center justify-center gap-1 mt-0.5 min-h-[16px]">
        <PlayerBadge pick={pick} />
      </div>
    </div>
  </Link>
);

export default function MyTeam() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-team"],
    queryFn: async () => (await api.get("/my-team")).data,
    staleTime: 30_000,
    // Live poll every 60s when any of the user's fixtures are in-play — mirrors
    // the Live Rank Tracker / Bonus Point Predictor pattern. Stop polling when
    // the whole GW is finalized (or nothing has kicked off yet).
    refetchInterval: (q) =>
      q.state.data?.any_fixture_live && !q.state.data?.event_finalized ? 60_000 : false,
    refetchIntervalInBackground: false,
  });

  if (isLoading) return <div className="totem-card h-96 animate-pulse mt-4" />;

  if (error) {
    return (
      <div className="totem-card p-6 text-totem-red">
        {String(error?.response?.data?.detail || error.message)}
      </div>
    );
  }

  if (data?.message) {
    return (
      <div className="totem-card p-6 text-center text-totem-textDim animate-fade-up">
        <div className="display-heading text-xl mb-2 text-totem-text">Pre-season</div>
        <p>{data.message}</p>
      </div>
    );
  }

  const rows = [[], [], [], []];
  data.starters.forEach((s) => rows[positionRow(s.element_type)].push(s));
  const benchBoostActive = data.bench_boost_active;
  const anyLive = data.any_fixture_live;

  return (
    <div data-testid={T.myTeamPage} className="space-y-4 pt-1 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-caps">Free transfers</div>
          <div className="display-heading text-2xl text-totem-green">
            {data.manager?.free_transfers ?? 1}
          </div>
        </div>
        <div className="text-right">
          <div className="label-caps">In the bank</div>
          <div className="display-heading text-2xl text-totem-green">
            £{(data.manager?.bank || 0).toFixed(1)}m
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mono uppercase text-xs flex-wrap">
        <span className="px-3 py-1.5 rounded-full bg-totem-green text-totem-bg font-semibold">
          My Team
        </span>
        <span className="px-3 py-1.5 rounded-full bg-totem-surfaceElev border border-totem-border text-totem-textDim">
          GW{data.gw}
        </span>
        {anyLive && !data.event_finalized && (
          <span
            data-testid="myteam-live-indicator"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-totem-red/15 border border-totem-red/40 text-totem-red"
          >
            <Radio size={11} className="animate-pulse" /> Live
          </span>
        )}
      </div>

      {data.active_chip && (
        <div className="flex">
          <ChipBadge chip={data.active_chip} />
        </div>
      )}

      <section data-testid={T.myTeamPitch} className="pitch-bg p-3">
        {rows.map((row, idx) => (
          <div key={idx} className="flex justify-around items-start gap-1 mb-4 last:mb-0">
            {row.map((p) => <PlayerCard key={p.id} pick={p} />)}
          </div>
        ))}
      </section>

      <section data-testid={benchBoostActive ? "myteam-bench-boosted" : "myteam-bench"}>
        <div className="flex items-center justify-between mb-2">
          <div className={cn("label-caps", benchBoostActive && "text-totem-green")}>
            {benchBoostActive ? "Bench (counts toward total)" : "Substitutes"}
          </div>
          <div className="mono text-[10px] uppercase text-totem-textDim">
            GW{data.gw} selection
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2 totem-card p-4">
          {data.bench.map((p) => (
            <PlayerCard
              key={p.id}
              pick={{ ...p, multiplier: benchBoostActive ? 1 : p.multiplier }}
            />
          ))}
        </div>
      </section>

      {data.entry_history && (
        <section className="totem-card p-4 grid grid-cols-4 gap-2">
          <div>
            <div className="label-caps">Points</div>
            <div className="mono text-lg text-totem-green">
              {data.entry_history.points}
            </div>
          </div>
          <div>
            <div className="label-caps">Bench</div>
            <div className="mono text-lg">{data.entry_history.points_on_bench}</div>
          </div>
          <div>
            <div className="label-caps">Rank</div>
            <div className="mono text-lg">
              {data.entry_history.rank ? data.entry_history.rank.toLocaleString() : "—"}
            </div>
          </div>
          <div>
            <div className="label-caps">Value</div>
            <div className="mono text-lg">
              £{((data.entry_history.value || 0) / 10).toFixed(1)}m
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
